import { useTranslation } from 'next-i18next';

import { zusToast } from '@zep/toast';

import type { SearchQuizsetRes } from '@/api/gen/_model/searchQuizsetRes';

import { useAddFolderItem } from '../api';
import { showFolderQuizPickerModal } from '../showFolderQuizPickerModal';
import { askPublishConsent } from '../utils/askPublishConsent';
import { QUIZ_SAVE_TOAST_POSITION } from '../utils/quizSaveFeedback';
import { withPublishConfirm } from '../utils/withPublishConfirm';

/**
 * 폴더에 퀴즈를 담는 흐름. 고르는 화면은 `showFolderQuizPickerModal` 이고, 여기서는 담기 요청과 공개 확인만 갖는다.
 *
 * 폴더 상세의 `퀴즈 추가`(빈 폴더의 `내 퀴즈에서 추가` 포함)가 쓴다. 폴더 수정 화면의 `퀴즈 추가` 는 서버에 바로 담지 않고
 * 초안에 쌓으므로 이 흐름이 아니라 피커만 연다(`FolderItemsSection`).
 */
export const useAddQuizFlow = (folderId: string) => {
  const { t } = useTranslation(['common']);
  const addItem = useAddFolderItem();

  /** 담겼으면 `true`. 실패하거나 사용자가 공개 확인을 취소하면 `false` — 모달은 어느 쪽이든 열려 있고 행만 남긴다. */
  const addQuiz = async (quiz: SearchQuizsetRes) => {
    try {
      const outcome = await withPublishConfirm({
        send: confirmSourcePublication =>
          addItem.mutateAsync({
            id: folderId,
            quizsetId: quiz.id,
            confirmSourcePublication,
          }),
        ask: () => askPublishConsent(t),
      });
      if (!outcome.sent) return false;
    } catch {
      // 전역 처리기는 인증 실패만 본다 — 여기서 알리지 않으면 아무 일도 안 난 듯 보인다.
      zusToast.error(t('common:folder.edit.addItem.error'), {
        position: QUIZ_SAVE_TOAST_POSITION,
      });
      return false;
    }

    zusToast.success(t('common:folder.edit.addItem.success'), {
      position: QUIZ_SAVE_TOAST_POSITION,
    });
    return true;
  };

  return {
    /** `excludeQuizsetIds` 는 이미 담긴 퀴즈다. 방금 담아 목록이 아직 갱신되지 않은 순간을 막는다. */
    openAddQuizPicker: (excludeQuizsetIds: string[]) =>
      showFolderQuizPickerModal({ excludeQuizsetIds, addQuiz }),
  };
};
